import { useQuery } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { useOrganization } from 'hooks';
import useSafetyIncidentsEnabled from 'hooks/feature/safety/useSafetyIncidentsEnabled/useSafetyIncidentsEnabled';
import useSafetyObservationsEnabled from 'hooks/feature/safety/useSafetyObservationsEnabled/useSafetyObservationsEnabled';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import useReactQueryBaseKey from 'hooks/react-query/useReactQueryBaseKey/useReactQueryBaseKey';

export interface IPendingSafetyTasksAggregatedInfo {
  openObservations: number;
  openIncidents: number;
  incidentsNeedingReview: number;
}

const PENDING_SAFETY_TASK_COUNTS_QUERY = gql`
  query PendingSafetyTaskCounts(
    $includeOpenObservations: Boolean!
    $includeOpenIncidents: Boolean!
    $includeIncidentsNeedingReview: Boolean!
  ) {
    dashboard {
      pendingSafetyTaskCounts(
        includeOpenObservations: $includeOpenObservations
        includeOpenIncidents: $includeOpenIncidents
        includeIncidentsNeedingReview: $includeIncidentsNeedingReview
      ) {
        openObservations
        openIncidents
        incidentsNeedingReview
      }
    }
  }
`;

export default function usePendingSafetyTasksData() {
  const graphQLClient = useGraphQLClient();
  const baseKey = useReactQueryBaseKey();
  const organization = useOrganization();
  const isSafetyIncidentsEnabled = useSafetyIncidentsEnabled();
  const isSafetyObservationsEnabled = useSafetyObservationsEnabled();

  const includeOpenObservations = isSafetyObservationsEnabled;
  const includeOpenIncidents = !!organization.safetySignature;
  const includeIncidentsNeedingReview = isSafetyIncidentsEnabled;

  const enabled = isSafetyIncidentsEnabled || isSafetyObservationsEnabled;

  const { data } = useQuery({
    queryKey: [
      ...baseKey,
      'pending-safety-tasks',
      { includeOpenObservations, includeOpenIncidents, includeIncidentsNeedingReview },
    ],
    queryFn: async () => {
      const result = await graphQLClient.request<{
        dashboard: { pendingSafetyTaskCounts: IPendingSafetyTasksAggregatedInfo };
      }>({
        document: PENDING_SAFETY_TASK_COUNTS_QUERY,
        variables: { includeOpenObservations, includeOpenIncidents, includeIncidentsNeedingReview },
      });
      return result.dashboard.pendingSafetyTaskCounts;
    },
    enabled,
  });

  return {
    openObservations: data?.openObservations,
    openIncidents: data?.openIncidents,
    incidentsNeedingReview: data?.incidentsNeedingReview,
  };
}
